import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

export const DEFAULT_BANK_MEMO_INSTRUCTIONS = `A payer sometimes puts remittance detail in the memo of an ACH or wire: which invoices (or POs, orders, credit memos) the payment covers, often with an amount per line, sometimes as X12 RMR/REF segments, sometimes as free text.
Decide whether this memo carries such detail, enough for an AR clerk to apply the payment to specific invoices.
Payers number invoices differently, so do not require a particular format.
A memo that only names the payer, the bank, a batch or trace number, or a generic phrase like "ACH PAYMENT" or "INVOICE PAYMENT" does not.`;

/**
 * Classify Bank Memo Step
 *
 * Judges, with an LLM, whether one cash receipt's bank memo says what the
 * payment pays for. Takes one entry of the ingest step's `receipts` output
 * (it fans out over the array), records the verdict on the receipt, and
 * returns it for a Decision step to branch on. It also lists the invoice
 * numbers the memo names, so the submit step can spell them out for
 * extraction (a bank flattens "INV 126739R,128001" into one string). A
 * receipt with no memo is judged false without a model call.
 *
 * What counts as remittance detail is the org's call: `instructions` replaces
 * the default description for payers whose memos need a narrower or wider
 * reading.
 */
export const classifyBankMemoStep = defineStepConfig({
  type: "classify_bank_memo",
  name: "Classify Bank Memo",
  description:
    "Judge with AI whether a cash receipt's bank memo carries remittance detail (which invoices the payment covers)",
  category: "AI & Agents",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    receipt: z
      .object({
        bankTransactionId: z.string(),
        memo: z.string().nullable(),
      })
      .describe(
        "One cash receipt and its memo (an entry of the ingest step's `receipts`)",
      ),
  }),

  outputSchema: z.object({
    explainsPayment: z
      .boolean()
      .describe(
        "True when the memo carries remittance detail at or above the confidence threshold",
      ),
    confidence: z.number().describe("The model's confidence, 0 to 1"),
    reason: z.string().describe("One sentence on why"),
    invoiceNumbers: z
      .array(z.string())
      .describe(
        "The invoice (or PO, order, credit memo) numbers the memo names, one per entry, as written",
      ),
    bankTransactionId: z
      .string()
      .describe("The judged receipt's id (passed through for wiring)"),
  }),

  configSchema: z.object({
    instructions: z
      .string()
      .max(10_000)
      .default(DEFAULT_BANK_MEMO_INSTRUCTIONS)
      .describe("What counts as remittance detail in a memo"),
    confidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .default(0.75)
      .describe("Minimum confidence for explainsPayment to be true"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 3,
      initialInterval: "5 seconds",
      backoffCoefficient: 2,
    },
  },
});
